// Nomad-mon Go! — v12 FORGED CARD RENDER: the one place any screen gets a
// Duo/Fusion card's look from (Collection, forge sheet, squad carousel, forge
// ceremony, battle cut-in). Screens own their layout; this module owns the card:
//
//   forgedLayersHtml(d, opts)  markup to drop INSIDE a card's art box (the box
//                              must be position:relative, card-shaped): the
//                              living VIDEO (lazy, poster = still), the tier
//                              frame for a Fusion / the seam frame for a Duo.
//   wireForgedVideos(root)     start/stop those videos by visibility (max 4 play)
//   attachForgedFx(el, d)      the shared WebGL effect layer on the card in focus
//   detachForgedFx(el)
//
// A Duo's video IS its signature effect (scripts/forged-duo-media.py bakes the
// split breathing apart into each source card and snapping back). A Fusion's
// video is its face card's clip; the over-the-top part is the shared layer.

import { fusionFrameHtml, duoFrameHtml, ensureForgedFrameCss } from './forged-frame.js';
import { clipSrcUrl, artSrc } from './types.js';
export { attachForgedFx, detachForgedFx, forgedFxBurst, forgedFxState } from './forged-fx.js';

const V = 'v188';
const MAX_PLAYING = 3;
const hash = (s) => { let h = 7; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** Video + frame layers for a forged card. opts.frame=false skips the frame;
 *  opts.video=false skips the video (a locked tile); opts.autoplay plays now. */
export function forgedLayersHtml(d, opts = {}) {
  if (!d || !d.forged) return '';
  ensureForgedFrameCss();
  // grid-sized faces: a Duo plays its tile-sized cycle clip (1/4 the pixels to
  // decode); a Fusion tile stays a still (its video + FX live in focus views)
  const thumb = (opts.size || 'thumb') === 'thumb';
  // GRID TILES never decode video (measured: two playing tile videos halve the
  // grid's fps at 4x CPU). A Duo tile cycles A -> strips -> B -> strips -> A
  // with two stacked stills and compositor-only opacity keyframes (no paint,
  // no main thread); a Fusion tile stays a still. The liquid video cycle plays
  // in focus views, the carousel, the forge reveal and battle.
  if (thumb && opts.video !== true) {
    const s = opts.srcs;
    const cyc = d.forged === 'duo' && s && s[1]
      ? `<span class="dCyc" aria-hidden="true" style="--dd:${(-(hash(d.id) % 88) / 10).toFixed(1)}s"><img class="s" alt="" loading="lazy" decoding="async" src="${esc(artSrc(s[1], V, 'thumb'))}">`
        + `<img class="f" alt="" loading="lazy" decoding="async" src="${esc(artSrc(s[1], V, 'thumb'))}"></span>` : '';
    const frame = opts.frame === false ? '' : d.forged === 'fusion' ? fusionFrameHtml(d.tier || 1) : duoFrameHtml();
    return cyc + frame;
  }
  const want = opts.video !== false;
  const clip = want ? clipSrcUrl(d.clip, V) : null;
  const video = clip
    ? `<video class="ffv" muted loop playsinline preload="none" disablepictureinpicture aria-hidden="true"
        poster="${esc(artSrc(d, V, opts.size || 'thumb'))}" data-src="${esc(clip)}"${opts.autoplay ? ' data-auto="1"' : ''}></video>`
    : '';
  const frame = opts.frame === false ? '' : d.forged === 'fusion' ? fusionFrameHtml(d.tier || 1) : duoFrameHtml();
  return video + frame;
}

let io = null;
const playing = [];
function start(v) {
  for (let i = playing.length - 1; i >= 0; i--) if (!playing[i].isConnected) playing.splice(i, 1);
  if (!v.getAttribute('src')) { v.src = v.dataset.src; }
  const p = v.play();
  if (p && p.catch) p.catch(() => {});
  v.onplaying = () => v.classList.add('on');
  const i = playing.indexOf(v); if (i >= 0) playing.splice(i, 1);
  playing.push(v);
  while (playing.length > MAX_PLAYING) stop(playing[0]);
}
function stop(v) {
  v.pause();
  const i = playing.indexOf(v); if (i >= 0) playing.splice(i, 1);
}

/** Play forged videos under `root` while they are on screen. Safe to call again
 *  after re-rendering; videos flagged data-auto start immediately. */
export function wireForgedVideos(root) {
  if (!root) return;
  const vids = root.querySelectorAll('video.ffv');
  if (!vids.length) return;
  if (!io && typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver((ents) => {
      for (const e of ents) (e.isIntersecting && e.intersectionRatio > 0.4 ? start : stop)(e.target);
    }, { threshold: [0, 0.4, 0.8] });
  }
  for (const v of vids) {
    if (v.dataset.auto) start(v);
    if (io && !v._ffo) { v._ffo = 1; io.observe(v); }
  }
}

// ---------------------------------------------------------------- LENTICULAR
// A Duo in a focus view with tilt on behaves like lenticular plastic: tilt one
// way and it IS card A, the other way card B, and through the middle the two
// interleave in thin vertical strips under a sliding sheen. While tilt drives
// it, the baked cycle video underneath pauses; ~1.2 s after the tilt settles
// back to centre, the lens fades out and the cycle resumes.
// `art` = the Duo's art box (position:relative); `srcs` = its two source card
// defs; `p` = the lens position, -1 (all A) .. 0 (half strips) .. 1 (all B).
// The caller maps its input to p (main.js: ~±14° of side tilt, a finger drag
// across the card, or the cursor). One lens per art box, made on first use;
// cost while active = one mask repaint + one transform per frame, on ONE element.
export function duoLensTilt(art, srcs, p, driving) {
  if (!art || !srcs || srcs.length !== 2) return;
  let L = art._lens;
  if (!L) {
    if (!driving) return;
    const el = document.createElement('span');
    el.className = 'dLens';
    el.setAttribute('aria-hidden', 'true');
    const media = (c, cls) => {
      const clip = clipSrcUrl(c.clip, V);
      return clip ? `<video class="${cls}" muted loop playsinline preload="auto" src="${esc(clip)}" poster="${esc(artSrc(c, V))}"></video>`
        : `<img class="${cls}" alt="" src="${esc(artSrc(c, V))}">`;
    };
    el.innerHTML = media(srcs[0], 'a') + media(srcs[1], 'b') + '<i></i>';
    art.appendChild(el);
    L = art._lens = { el, b: el.querySelector('.b'), sheen: el.querySelector('i'), on: false, idleAt: 0, lb: -1 };
  }
  const now = performance.now();
  if (driving) L.idleAt = now;
  const on = now - L.idleAt < 1200;
  if (on !== L.on) {
    L.on = on;
    L.el.classList.toggle('on', on);
    const cyc = art.querySelector('video.ffv');
    for (const v of L.el.querySelectorAll('video')) { if (on) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause(); }
    if (cyc) { if (on) cyc.pause(); else { const p = cyc.play(); if (p && p.catch) p.catch(() => {}); } }
  }
  if (!on) return;
  const q = Math.max(-1, Math.min(1, p));
  const lb = 0.5 + 0.5 * q;
  if (Math.abs(lb - L.lb) > 0.004) { L.lb = lb; L.b.style.setProperty('--lb', lb.toFixed(3)); }
  L.sheen.style.transform = `translate3d(${(q * 30).toFixed(1)}%,0,0)`;
}
/** The lens state of an art box, for QA. */
export function duoLensState(art) { const L = art && art._lens; return L ? { on: L.on, lb: L.lb } : null; }
